/* eslint-disable react/no-unstable-nested-components */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, connect } from 'react-redux';
import { compose } from 'redux';
import ProvidersPopup from 'backoffice/users/_components/ProvidersPopup';
import { FormattedMessage } from 'react-intl';

import { useMediaQuery } from 'react-responsive';

import { BOPaginationTable, DateCell, MoneyCell, ActionsButton } from 'common/components';
import { ColumnFilterHeader } from '../ContactsFilters/ContactsFilters';
import { useContactsFilters } from '../ContactsFilters/ContactsFiltersContext';
import { columnFilters } from '../ContactsFilters/messages';
import IconPhone from './iconPhone';
import Tags from '../Tags';
import NewFullNameCell from './NewFullNameCell/NewFullNameCell';
import SelectCheckbox from '../SelectCheckbox';

import { getContacts, getContactsFavorites, crmSetSelectedContactsIds } from '../../_redux';

import { staticStyles } from './style';

const NewContactsTable = ({
  getContacts,
  getContactsFavorites,
  onTagSelect,
  contacts,
  contactsAreLoaded,
  contactsError,
  contactsTotalsAreLoaded,
  contactsTotalsError,
  contactsRevision,
  profilePerms,
  addons,
}) => {
  const { text, params: query } = useContactsFilters();
  const isMobile = useMediaQuery({ query: '(max-width: 1050px)' });
  const newContactsTableRef = useRef();
  const dispatch = useDispatch();

  const withClick2Call = (!!profilePerms.managerViewUser || !!profilePerms.viewUser) && addons.includes('click2call');

  const [isCallModalOpen, setCallModalOpen] = useState(false);
  const [buttonPosition, setButtonPosition] = useState([0, 0]);
  const [modalId, setModalId] = useState(0);

  const getPopupPosition = (buttonPosition, tablePosition) => {
    const topDiff = isMobile ? 30 : 0;
    const rightDiff = isMobile ? 80 : 210;
    const top = buttonPosition.y - tablePosition.y + topDiff;
    const right = buttonPosition.x - tablePosition.x - rightDiff;
    setButtonPosition([top, right]);
  };

  const toggleCallModalOpen = (id, elem, isHover) => {
    if (isHover) {
      setCallModalOpen(true);
    } else {
      setCallModalOpen(!isCallModalOpen);
    }

    if (id) {
      setModalId(id);
      getPopupPosition(elem.getBoundingClientRect(), newContactsTableRef.current.getBoundingClientRect());
    }
  };
  const totalDiff =
    contacts.total_in_sum && contacts.total_out_sum
      ? {
          amount: Number(contacts.total_in_sum.amount) - Number(contacts.total_out_sum.amount),
          currency: contacts.total_in_sum.currency,
        }
      : null;

  const columns = [
    {
      id: 'justActions',
      header: () => <SelectCheckbox header />,
      cell: ({
        row: {
          original: { id },
        },
      }) => <SelectCheckbox contactId={id} />,
      size: 80,
    },
    {
      headerKey: 'justName',
      accessorKey: 'fullName',
      cell: ({
        row: {
          original: { id, fullName, isManager },
        },
      }) => <NewFullNameCell id={id} fullName={fullName} isManager={isManager} />,
      size: 240,
    },
    {
      headerKey: 'justLastSeen',
      accessorKey: 'lastSeen',
      cell: ({
        row: {
          original: { lastSeen },
        },
      }) => (lastSeen ? <DateCell value={lastSeen} hideTime grey /> : '—'),
      size: 150,
    },
    ...[
      ['justIn', 'totalIn'],
      ['justOut', 'totalOut'],
      ['justInAndOut', 'totalDiff'],
      ['justBalance', 'balance'],
    ].map(([headerKey, field]) => ({
      headerKey,
      accessorKey: field === 'balance' ? undefined : field,
      cell: ({ row: { original } }) => <MoneyCell crmStyle heightMaxContent value={original[field]} />,
      size: 150,
    })),
    {
      headerKey: 'crmSalesStatus',
      cell: ({
        row: {
          original: { status },
        },
      }) =>
        status ? (
          <span className="NewContactsTable__status">
            <span className="NewContactsTable__status-dot" style={{ backgroundColor: status.color }} />
            {status.title}
          </span>
        ) : (
          '—'
        ),
      size: 160,
    },
    {
      headerKey: 'justTags',
      cell: ({
        row: {
          original: { id, tags },
        },
      }) => <Tags tags={tags || []} onTagSelect={onTagSelect} id={id} />,
      size: 220,
    },
    {
      headerKey: 'justLastComment',
      label: text('lastCommentContactFilter'),
      cell: ({
        row: {
          original: { lastTask },
        },
      }) => lastTask?.comment || '—',
      size: 280,
    },
    ...(profilePerms.viewNotes || profilePerms.managerViewNotes
      ? [
          {
            headerKey: 'lastNoteText',
            label: text('lastNoteContactFilter'),
            cell: ({ row: { original } }) => original.lastNoteText || '—',
            size: 280,
          },
        ]
      : []),
    ...(withClick2Call
      ? [
          {
            headerKey: 'justCall',
            cell: ({
              row: {
                original: { id },
              },
            }) => (
              <ActionsButton
                onClickFunc={evt => {
                  evt.stopPropagation();
                  toggleCallModalOpen(id, evt.currentTarget);
                }}
                onMouseEnterFunc={evt => {
                  evt.stopPropagation();
                  toggleCallModalOpen(id, evt.currentTarget, true);
                }}>
                <IconPhone />
              </ActionsButton>
            ),
            size: 90,
          },
        ]
      : []),
  ];

  useEffect(() => {
    setCallModalOpen(false);
    dispatch(crmSetSelectedContactsIds([]));
    getContacts({ search: query });
  }, [query, contactsRevision]);

  useEffect(() => {
    getContactsFavorites();
  }, []);

  return (
    <div className="NewContactsTable" ref={newContactsTableRef}>
      {contactsError && (
        <p role="alert" className="ContactsFilters__error">
          {text('error')}{' '}
          <button type="button" onClick={() => getContacts({ search: query })}>
            {text('retry')}
          </button>
        </p>
      )}
      {contactsAreLoaded && !contactsError && (
        <div className="NewContactsTable__totals" aria-busy={!contactsTotalsAreLoaded}>
          {contactsTotalsError && (
            <p role="alert" className="ContactsFilters__error">
              {text('totalsError')}{' '}
              <button type="button" onClick={() => getContacts({ search: query })}>
                {text('retry')}
              </button>
            </p>
          )}
          {[
            ['justIn', contacts.total_in_sum],
            ['justOut', contacts.total_out_sum],
            ['justInAndOut', totalDiff],
          ].map(([label, value]) => (
            <div className="NewContactsTable__total" key={label}>
              <FormattedMessage id={label} />
              <MoneyCell
                crmStyle
                heightMaxContent
                value={value}
                loading={!contactsTotalsAreLoaded}
                loadingFailed={!!contactsTotalsError}
              />
            </div>
          ))}
        </div>
      )}
      <BOPaginationTable
        tableKey="CRMContactsTable"
        data={{ ...contacts, results: contacts.results || [], count: contacts.count || 0 }}
        columns={columns.map(column => ({
          ...column,
          ...(columnFilters[column.headerKey]
            ? {
                header: (
                  <ColumnFilterHeader
                    filterKey={columnFilters[column.headerKey]}
                    label={column.label || <FormattedMessage id={column.headerKey} />}
                  />
                ),
              }
            : {}),
        }))}
        params={{ search: query }}
        loading={!contactsAreLoaded}
        emptyMessage={contactsError ? null : <FormattedMessage id="crmThereAreNoItemsMatchingParameters" />}
      />
      {isCallModalOpen ? (
        <ProvidersPopup id={modalId} buttonPosition={buttonPosition} toggleModalOpen={toggleCallModalOpen} />
      ) : null}
      <style jsx global>
        {staticStyles}
      </style>
    </div>
  );
};

NewContactsTable.propTypes = {
  onTagSelect: PropTypes.func.isRequired,
  profilePerms: PropTypes.object.isRequired,
  addons: PropTypes.array.isRequired,
  contacts: PropTypes.object.isRequired,
  contactsAreLoaded: PropTypes.bool.isRequired,
  contactsError: PropTypes.any,
  contactsTotalsAreLoaded: PropTypes.bool.isRequired,
  contactsTotalsError: PropTypes.any,
  contactsRevision: PropTypes.number,
  getContacts: PropTypes.func.isRequired,
  getContactsFavorites: PropTypes.func.isRequired,
};

NewContactsTable.defaultProps = { contactsError: null, contactsTotalsError: null, contactsRevision: 0 };

export default compose(
  connect(
    state => ({
      contacts: { ...state.crm.contacts.contacts, ...state.crm.contacts.contactsTotals },
      contactsAreLoaded: state.crm.contacts.contactsAreLoaded,
      contactsError: state.crm.contacts.contactsError,
      contactsTotalsAreLoaded: state.crm.contacts.contactsTotalsAreLoaded,
      contactsTotalsError: state.crm.contacts.contactsTotalsError,
      contactsRevision: state.crm.contacts.contactsRevision,
      profilePerms: state.user.profile.perms,
      addons: state.interfaceConfig.addons,
    }),
    {
      getContacts: search => getContacts.request(search),
      getContactsFavorites: () => getContactsFavorites.request(),
    }
  )
)(NewContactsTable);

export { NewContactsTable };
